import {
  DropdownButton,
  DropdownButtonContext,
  EmojiPicker,
  FieldLabel,
  FlexBox,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Switch,
  Text,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useEffectOnce } from 'react-use';
import { v4 as uuid } from 'uuid';

import { AwayStatus, presenceApi } from 'apis/presence';
import { HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';

import styles from './awayStatusModal.module.scss';

const formId = uuid();

type FormFields = Pick<
  AwayStatus,
  'emoji' | 'label' | 'duration_limit_minutes'
>;

export const AwayStatusModal = createAppModal(
  { type: 'settings:awayStatus' },
  ({ isOpen, modal, hide }): JSX.Element => {
    const { awayStatus } = modal.arg;
    const form = useForm<FormFields>({
      mode: 'all',
      defaultValues: {
        emoji: awayStatus?.emoji || '',
        label: awayStatus?.label || '',
        duration_limit_minutes: awayStatus?.duration_limit_minutes || null,
      },
    });
    const [durationLimitChecked, setDurationLimitChecked] = useState<boolean>(
      awayStatus?.duration_limit_minutes != null,
    );
    const notify = useNotifications();
    const { isSubmitting, isDirty } = form.formState;
    const emoji = form.watch('emoji');

    const [createAwayStatus] = presenceApi.useCreateAwayStatusMutation();
    const [updateAwayStatus] = presenceApi.useUpdateAwayStatusMutation();

    const onDurationLimitCheckedChanged = () => {
      const newValue = !durationLimitChecked;
      setDurationLimitChecked(newValue);
      if (!newValue) {
        form.setValue('duration_limit_minutes', null, { shouldDirty: true });
      }
    };

    const handleSubmit = async (form: FormFields) => {
      const isSuccess = await (awayStatus
        ? handleUpdate(form, awayStatus.id)
        : handleCreate(form));

      if (isSuccess) {
        hide?.();
        notify({
          title: awayStatus ? 'Away status updated' : 'Away status created',
          text: awayStatus
            ? 'Successfully updated away status'
            : 'Successfully created away status',
          time: 5000,
          type: 'success',
          icon: 'done',
          status: 'success',
        });
      } else {
        notify(
          {
            title: awayStatus
              ? 'Error while updating away status.'
              : 'Error while creating away status.',
            text: 'Something went wrong.',
            icon: 'exclamation',
            className: 'danger',
            time: 5000,
          },
          NotificationType.Error,
        );
      }
    };

    const handleCreate = async (form: FormFields) => {
      const response = await createAwayStatus(form);
      const isSuccess = isResponseWithData(response);
      if (isSuccess) {
        ampli.settingsCustomAwayStatusCreated();
      }

      return isSuccess;
    };

    const handleUpdate = async (form: FormFields, statusId: string) => {
      const response = await updateAwayStatus({
        ...form,
        id: statusId,
      });
      const isSuccess = isResponseWithData(response);
      if (isSuccess) {
        ampli.settingsCustomAwayStatusUpdated();
      }

      return isSuccess;
    };

    useEffectOnce(() => {
      if (awayStatus) return;

      const { unsubscribe } = form.watch((data, { name, type }) => {
        if (
          !data.emoji &&
          type === 'change' &&
          name === 'label' &&
          (data.label?.length ?? 0) > 0
        ) {
          unsubscribe();
          // Set default 💬 emoji for a new away status as soon as user starts typing.
          form.setValue('emoji', '\uD83D\uDCAC');
        }
      });

      return unsubscribe;
    });

    return (
      <Modal className={styles.root} isOpen={isOpen} onClose={hide}>
        <ModalHead
          title={awayStatus ? 'Edit away status' : 'Create new away status'}
          onClose={hide}
        />
        <ModalBody>
          <FormProvider {...form}>
            <form
              id={formId}
              noValidate
              tabIndex={-1}
              onSubmit={form.handleSubmit(handleSubmit)}
            >
              <FormGrid className={styles.form}>
                <FormField span={6}>
                  <HookFormText
                    name="label"
                    label="Away status label"
                    control={form.control}
                    rules={{
                      required: 'Label is required',
                    }}
                    componentProps={{
                      placeholder: 'Write the label of your status',
                      prefix: {
                        type: 'custom',
                        noWrap: true,
                        value: (
                          <FlexBox alignItems="center">
                            <DropdownButton
                              className={styles.dropdown}
                              icon={emoji ? undefined : 'smile'}
                              buttonProps={{ variant: 'tertiary' }}
                              noArrow
                              fixed
                              dropdown={
                                <DropdownButtonContext.Consumer>
                                  {({ toggleDropdown }) => (
                                    <EmojiPicker
                                      onSelect={(value) => {
                                        form.setValue('emoji', value, {
                                          shouldDirty: true,
                                        });
                                        toggleDropdown();
                                      }}
                                    />
                                  )}
                                </DropdownButtonContext.Consumer>
                              }
                            >
                              {emoji && (
                                <Text.Paragraph>{emoji}</Text.Paragraph>
                              )}
                            </DropdownButton>
                          </FlexBox>
                        ),
                      },
                      autoFocus: true,
                      type: 'text',
                    }}
                  />
                </FormField>
                <FormField span={3}>
                  <FlexBox gap="200" alignItems="center">
                    <FieldLabel htmlFor="duration-limit-checked">
                      Enable duration limit
                    </FieldLabel>
                    <Switch
                      name="duration-limit-checked"
                      id="duration-limit-checked"
                      value={`${durationLimitChecked}`}
                      checked={durationLimitChecked}
                      onChange={onDurationLimitCheckedChanged}
                    />
                  </FlexBox>
                </FormField>
                <FormField span={3}>
                  <HookFormText
                    name="duration_limit_minutes"
                    label="Duration limit (minutes)"
                    disabled={!durationLimitChecked}
                    control={form.control}
                    rules={{
                      min: {
                        value: 1,
                        message: 'The minimum duration limit is 1 minute',
                      },
                    }}
                    componentProps={{
                      type: 'number',
                      placeholder: 'Duration limit in minutes',
                    }}
                  />
                </FormField>
              </FormGrid>
            </form>
          </FormProvider>
        </ModalBody>

        <ModalFoot
          cancelOnClick={hide}
          confirmName={awayStatus ? 'Update status' : 'Create status'}
          confirmOnClick={noop}
          confirmProps={{
            type: 'submit',
            disabled: isSubmitting || !isDirty,
            isLoading: isSubmitting,
            loadingIndicator: awayStatus ? 'Updating...' : 'Creating...',
            form: formId,
          }}
        />
      </Modal>
    );
  },
);
